import * as stylex from '@stylexjs/stylex';
import { darkTokens as tokens } from './tokens.stylex';

// Media query constants for reuse
const MEDIA_768 = '@media (max-width: 768px)';
const MEDIA_480 = '@media (max-width: 480px)';

// Keyframes
const copySuccess = stylex.keyframes({
    '0%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.1)' },
    '100%': { transform: 'scale(1)' },
});

export const styles = stylex.create({
    modelPage: {
        maxWidth: 1200,
        margin: '0 auto',
        padding: 'var(--spacing-lg)',
        color: 'var(--text-primary)',
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transition: 'color var(--transition-fast)',
        ':hover': {
            color: 'var(--primary)',
        },
    },
    pageTitle: {
        fontSize: '2.5rem',
        fontWeight: 700,
        margin: 0,
        color: 'var(--text-primary)',
        '@media (max-width: 768px)': {
            fontSize: '2rem',
        },
        '@media (max-width: 480px)': {
            fontSize: '1.75rem',
        },
    },

    /* Hero Section Base */
    heroSection: {
        position: 'relative',
        height: 600,
        borderRadius: 'var(--radius-xl)',
        marginBottom: 'var(--spacing-3xl)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        '@media (max-width: 768px)': {
            height: 400,
            minHeight: 400,
        },
        '@media (max-width: 480px)': {
            height: 350,
            minHeight: 350,
        },
    },
    heroBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
    },

    heroBgImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center right',
    },

    heroOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 40%, rgba(0, 0, 0, 0.3) 70%, transparent 100%)',
    },

    muteIndicator: {
        position: 'absolute',
        bottom: 20,
        right: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '50%',
        padding: 12,
        color: 'white',
        opacity: 0.7,
        transition: 'opacity 0.3s ease, transform 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    muteIndicatorHover: {
        opacity: 1,
        transform: 'scale(1.1)',
    },
    heroContent: {
        position: 'relative',
        zIndex: 2,
        padding: 'var(--spacing-2xl)',
        maxWidth: 600,
        color: 'white',
        '@media (max-width: 768px)': {
            padding: 'var(--spacing-md)',
            textAlign: 'left',
        },
        '@media (max-width: 480px)': {
            padding: 'var(--spacing-sm)',
        },
    },
    heroTitle: {
        fontSize: '3rem',
        fontWeight: 800,
        margin: '0 0 var(--spacing-md) 0',
        color: 'white',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        '@media (max-width: 768px)': {
            fontSize: '2.5rem',
            textAlign: 'left',
            lineHeight: 1.2,
        },
        '@media (max-width: 480px)': {
            fontSize: '1.75rem',
            lineHeight: 1.1,
        },
    },
    heroDescription: {
        fontSize: 'var(--font-size-lg)',
        color: 'rgba(255, 255, 255, 0.9)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        '@media (max-width: 768px)': {
            fontSize: '1rem',
            lineHeight: 1.5,
            marginBottom: 'var(--spacing-lg)',
        },
        '@media (max-width: 480px)': {
            fontSize: '0.9rem',
            lineHeight: 1.4,
        },
    },
    heroStats: {
        display: 'flex',
        gap: 'var(--spacing-lg)',
        flexWrap: 'wrap',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: 'var(--spacing-md)',
            alignItems: 'flex-start',
        },
        '@media (max-width: 480px)': {
            gap: 'var(--spacing-sm)',
        },
    },
    stat: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'rgba(255, 255, 255, 0.9)',
        fontWeight: 500,
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        '@media (max-width: 768px)': {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            minWidth: 200,
        },
        '@media (max-width: 480px)': {
            minWidth: 180,
            fontSize: '0.9rem',
        },
    },
    statSvg: {
        color: 'var(--accent)',
        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))',
    },
    creditCostBadge: {
        backgroundColor: 'var(--accent)',
        color: 'var(--text-on-accent)',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginTop: 'var(--spacing-md)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        display: 'inline-block',
        '@media (max-width: 768px)': {
            marginTop: 'var(--spacing-lg)',
            fontSize: '0.9rem',
        },
        '@media (max-width: 480px)': {
            fontSize: '0.8rem',
            padding: 'var(--spacing-xs) var(--spacing-sm)',
        },
    },


    /* Section Titles */
    sectionTitle: {
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 var(--spacing-xl) 0',
        color: 'var(--text-primary)',
        position: 'relative',
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 60,
            height: 3,
            backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
            borderRadius: 'var(--radius-full)',
        },
    },

    /* Gallery Styles */
    gallerySection: {
        marginBottom: 'var(--spacing-3xl)',
    },
    galleryDescription: {
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
        textAlign: 'left',
        whiteSpace: 'normal',
    },

    galleryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gridAutoRows: '280px',
        gridAutoFlow: 'dense',
        gap: tokens.spaceMd,
        [MEDIA_768]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: tokens.spaceSm,
            gridAutoRows: '200px',
        },
        [MEDIA_480]: {
            gridTemplateColumns: '1fr',
            gridAutoRows: 'auto',
        },
    },

    galleryItem: {
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-card)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        //borderRadius: 'var(--radius-md)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        transitionTimingFunction: 'ease-out',
        cursor: 'pointer',
        ':hover': {
            borderColor: 'var(--border)',
            boxShadow: `0 8px 30px rgba(0, 0, 0, 0.4), 0 0 15px ${tokens.accentLight}`,
            transform: 'translateY(-4px)',
        },
    },

    featured: {
        gridColumn: 'span 2',
        gridRow: 'span 2',
    },

    wide: {
        gridColumn: 'span 2',
    },

    tall: {
        gridRow: 'span 2',
    },

    galleryImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transitionProperty: 'transform',
        transitionDuration: tokens.transitionNormal,
    },

    galleryImageHover: {
        transform: 'scale(1.05)',
    },

    galleryPrompt: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.3) 80%, transparent 100%)',
        color: 'white',
        padding: 'var(--spacing-md)',
        transform: 'translateY(100%)',
        transitionProperty: 'transform',
        transitionDuration: tokens.transitionNormal,
        zIndex: 10,
    },

    galleryPromptVisible: {
        transform: 'translateY(0)',
    },

    galleryPromptText: {
        fontSize: '0.75rem',
        lineHeight: 1.3,
        display: 'block',
        fontWeight: 500,
    },

    /* Text Image to Image List Layout */
    textImageList: {
        display: 'flex',
        flexDirection: 'column',
        gap: "2.5rem",
        maxWidth: 1200,
        marginInline: 'auto',
        marginBlock: 0,
    },

    textImageItem: {
        cursor: 'pointer',
        position: 'relative',
        borderRadius: tokens.radiusLg,
        overflow: 'hidden',
        borderWidth: 0,
        borderStyle: 'none',
        borderColor: 'transparent',
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        transitionTimingFunction: 'ease-out',
        ':hover': {
            transform: 'scale(1.01)',
            boxShadow: `0 12px 40px rgba(0, 0, 0, 0.3), 0 0 20px ${tokens.accentLight}`,
        },
    },

    imagePair: {
        display: 'flex',
        gap: tokens.spaceSm,
        alignItems: 'center',
        justifyContent: 'center',
        [MEDIA_768]: {
            flexDirection: 'row',
        },
    },

    arrowSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingInline: 'var(--spacing-sm)',
        paddingBlock: 0,
    },

    arrow: {
        fontSize: '2.5rem',
        color: 'var(--primary)',
        fontWeight: 'bold',
        opacity: 0.8,
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
    },

    arrowHover: {
        opacity: 1,
        transform: 'scale(1.1)',
        color: 'var(--accent)',
    },

    referenceImage: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 'var(--radius-md)',
    },

    generatedImage: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 'var(--radius-md)',
    },

    comparisonImage: {
        width: 500,
        height: 500,
        objectFit: 'cover',
        borderRadius: 'var(--radius-md)',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        [MEDIA_768]: {
            width: 160,
            height: 160,
        },
    },

    comparisonImageHover: {
        transform: 'scale(1.02)',
    },

    /* Click indicator overlay */
    clickIndicator: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        opacity: 0,
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        pointerEvents: 'none',
        zIndex: 100,
        backdropFilter: 'blur(10px)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },

    clickIndicatorVisible: {
        opacity: 1,
        transform: 'translate(-50%, -50%) scale(1.1)',
    },

    imagePrompt: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.3) 80%, transparent 100%)',
        color: 'white',
        padding: 'var(--spacing-sm)',
        transform: 'translateY(100%)',
        transitionProperty: 'transform',
        transitionDuration: 'var(--transition-normal)',
        pointerEvents: 'none',
        zIndex: 10,
    },

    imagePromptVisible: {
        transform: 'translateY(0)',
    },

    imagePromptText: {
        fontSize: '0.75rem',
        lineHeight: 1.3,
        display: 'block',
        fontWeight: 500,
    },

    /* Multi-image reference layouts */
    referenceImages: {
        position: 'relative',
        width: 500,
        height: 500,
        [MEDIA_768]: {
            width: 160,
            height: 160,
        },
    },

    twoImages: {
        position: 'relative',
    },

    twoImagesTopLeft: {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 2,
        width: 300,
        height: 300,
        [MEDIA_768]: {
            width: 100,
            height: 100,
        },
    },

    twoImagesBottomRight: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        zIndex: 1,
        width: 300,
        height: 300,
        [MEDIA_768]: {
            width: 100,
            height: 100,
        },
    },

    fourImages: {
        position: 'relative',
    },

    fourImagesItem: {
        position: 'absolute',
        width: 200,
        height: 200,
        [MEDIA_768]: {
            width: 80,
            height: 80,
        },
    },

    fourImagesTopLeft: {
        top: 0,
        left: 0,
        zIndex: 4,
    },

    fourImagesTopRight: {
        top: 80,
        left: 180,
        zIndex: 3,
        [MEDIA_768]: {
            top: 30,
            left: 60,
        },
    },

    fourImagesBottomLeft: {
        bottom: 80,
        left: 20,
        zIndex: 2,
        [MEDIA_768]: {
            bottom: 30,
            left: 10,
        },
    },

    fourImagesBottomRight: {
        bottom: 0,
        right: 80,
        zIndex: 1,
        [MEDIA_768]: {
            right: 20,
        },
    },

    multiImageImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: tokens.radiusMd,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: tokens.border,
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
    },

    multiImageImgHover: {
        transform: 'scale(1.05)',
        zIndex: 10,
    },

    multiImagePrompt: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.3) 80%, transparent 100%)',
        color: 'white',
        padding: tokens.spaceSm,
        transform: 'translateY(100%)',
        transitionProperty: 'transform',
        transitionDuration: tokens.transitionNormal,
        zIndex: 20,
        opacity: 0,
    },

    multiImagePromptVisible: {
        transform: 'translateY(0)',
        opacity: 1,
    },

    /* Features Section */
    featuresSection: {
        marginBottom: tokens.space3xl,
    },

    featuresGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: tokens.spaceLg,
        [MEDIA_768]: {
            gridTemplateColumns: '1fr',
        },
    },

    featureCard: {
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceXl,
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        ':hover': {
            transform: 'translateY(-4px)',
            borderColor: tokens.primary,
            boxShadow: tokens.shadowLg,
        },
    },

    featureIcon: {
        width: 48,
        height: 48,
        backgroundColor: tokens.primaryBgLight,
        borderRadius: tokens.radiusLg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: tokens.spaceMd,
        color: tokens.primary,
    },

    featureCardTitle: {
        fontSize: tokens.fontLg,
        fontWeight: 600,
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: tokens.spaceSm,
        color: tokens.textPrimary,
    },

    featureCardText: {
        color: tokens.textSecondary,
        lineHeight: 1.6,
        margin: 0,
    },

    /* Model Variants Section */
    variantsSection: {
        marginBottom: tokens.space3xl,
    },

    variantsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: tokens.spaceLg,
        [MEDIA_768]: {
            gridTemplateColumns: '1fr',
        },
    },

    variantCard: {
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceXl,
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        ':hover': {
            transform: 'translateY(-2px)',
            boxShadow: tokens.shadowMd,
        },
    },

    variantCardPrimary: {
        borderColor: tokens.primary,
        backgroundColor: tokens.primaryBgSubtle,
    },

    variantHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: tokens.spaceMd,
    },

    variantHeaderTitle: {
        fontSize: tokens.fontLg,
        fontWeight: 600,
        margin: 0,
        color: tokens.textPrimary,
    },

    variantCardText: {
        color: tokens.textSecondary,
        lineHeight: 1.6,
        marginBottom: tokens.spaceMd,
    },

    badge: {
        paddingTop: tokens.spaceXs,
        paddingBottom: tokens.spaceXs,
        paddingLeft: tokens.spaceSm,
        paddingRight: tokens.spaceSm,
        borderRadius: tokens.radiusFull,
        fontSize: tokens.fontXs,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },

    badgePrimary: {
        backgroundColor: tokens.primary,
        color: tokens.textOnAccent,
    },

    badgeSecondary: {
        backgroundColor: tokens.secondary,
        color: tokens.textOnAccent,
    },

    variantFeatures: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },

    variantFeaturesItem: {
        color: tokens.textSecondary,
        paddingTop: tokens.spaceXs,
        paddingBottom: tokens.spaceXs,
        paddingLeft: tokens.spaceMd,
        paddingRight: 0,
        position: 'relative',
        '::before': {
            content: '"✓"',
            position: 'absolute',
            left: 0,
            color: tokens.success,
            fontWeight: 'bold',
        },
    },

    /* Special Feature Section */
    specialFeatureSection: {
        marginBottom: tokens.space3xl,
    },

    specialFeatureContent: {
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceXl,
    },

    specialFeatureInfoTitle: {
        fontSize: tokens.fontLg,
        fontWeight: 600,
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: tokens.spaceMd,
        color: tokens.textPrimary,
    },

    specialFeatureInfoText: {
        color: tokens.textSecondary,
        lineHeight: 1.6,
        marginBottom: tokens.spaceLg,
    },

    specialFeatureBenefits: {
        display: 'flex',
        gap: tokens.spaceLg,
        flexWrap: 'wrap',
        [MEDIA_768]: {
            flexDirection: 'column',
            gap: tokens.spaceMd,
        },
    },

    benefit: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spaceSm,
        color: tokens.textSecondary,
        fontWeight: 500,
    },

    benefitIcon: {
        color: tokens.accent,
    },

    /* Access Section */
    accessSection: {
        marginBottom: tokens.space3xl,
    },

    accessGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: tokens.spaceLg,
        [MEDIA_768]: {
            gridTemplateColumns: '1fr',
        },
    },

    accessCard: {
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceXl,
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        ':hover': {
            transform: 'translateY(-2px)',
            borderColor: tokens.primary,
            boxShadow: tokens.shadowMd,
        },
    },

    accessHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: tokens.spaceMd,
    },

    accessHeaderTitle: {
        fontSize: tokens.fontLg,
        fontWeight: 600,
        margin: 0,
        color: tokens.textPrimary,
    },

    accessCardText: {
        color: tokens.textSecondary,
        lineHeight: 1.6,
        marginBottom: tokens.spaceMd,
    },

    price: {
        backgroundColor: tokens.success,
        color: tokens.textOnAccent,
        paddingTop: tokens.spaceXs,
        paddingBottom: tokens.spaceXs,
        paddingLeft: tokens.spaceSm,
        paddingRight: tokens.spaceSm,
        borderRadius: tokens.radiusFull,
        fontSize: tokens.fontSm,
        fontWeight: 500,
    },

    accessFeatures: {
        display: 'flex',
        gap: tokens.spaceSm,
        flexWrap: 'wrap',
        marginBottom: tokens.spaceMd,
    },

    accessFeaturesItem: {
        backgroundColor: tokens.primaryBgSubtle,
        color: tokens.primary,
        paddingTop: tokens.spaceXs,
        paddingBottom: tokens.spaceXs,
        paddingLeft: tokens.spaceSm,
        paddingRight: tokens.spaceSm,
        borderRadius: tokens.radiusFull,
        fontSize: tokens.fontXs,
        fontWeight: 500,
    },

    platformList: {
        display: 'flex',
        gap: tokens.spaceSm,
        flexWrap: 'wrap',
        marginBottom: tokens.spaceMd,
    },

    platformListItem: {
        backgroundColor: tokens.primaryBgSubtle,
        color: tokens.secondary,
        paddingTop: tokens.spaceXs,
        paddingBottom: tokens.spaceXs,
        paddingLeft: tokens.spaceSm,
        paddingRight: tokens.spaceSm,
        borderRadius: tokens.radiusFull,
        fontSize: tokens.fontXs,
        fontWeight: 500,
    },

    accessLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spaceSm,
        color: tokens.primary,
        textDecoration: 'none',
        fontWeight: 500,
        transitionProperty: 'color',
        transitionDuration: tokens.transitionFast,
        ':hover': {
            color: tokens.primaryHover,
        },
    },

    /* Licensing Section */
    licensingSection: {
        marginBottom: tokens.space3xl,
    },

    licensingContent: {
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceXl,
    },

    licensingInfo: {
        display: 'grid',
        gap: tokens.spaceXl,
    },

    licenseCard: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spaceMd,
        padding: tokens.spaceLg,
        borderRadius: tokens.radiusLg,
    },

    licenseCardCommercial: {
        backgroundColor: tokens.primaryBgSubtle,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.primaryBorderLight,
    },

    licenseCardIcon: {
        color: tokens.primary,
        flexShrink: 0,
        marginTop: 2,
    },

    licenseCardTitle: {
        fontSize: tokens.fontLg,
        fontWeight: 600,
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: tokens.spaceSm,
        color: tokens.textPrimary,
    },

    licenseCardText: {
        color: tokens.textSecondary,
        lineHeight: 1.6,
        margin: 0,
    },

    licenseTypes: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: tokens.spaceLg,
        [MEDIA_768]: {
            gridTemplateColumns: '1fr',
        },
    },

    licenseTypeTitle: {
        fontSize: tokens.fontMd,
        fontWeight: 600,
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: tokens.spaceSm,
        color: tokens.textPrimary,
    },

    licenseTypeList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },

    licenseTypeItem: {
        color: tokens.textSecondary,
        paddingTop: tokens.spaceXs,
        paddingBottom: tokens.spaceXs,
        paddingLeft: tokens.spaceMd,
        paddingRight: 0,
        position: 'relative',
        '::before': {
            content: '"•"',
            position: 'absolute',
            left: 0,
            color: tokens.primary,
        },
    },

    /* Technical Specifications */
    techSpecsSection: {
        marginBottom: tokens.space3xl,
    },

    specsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: tokens.spaceLg,
        [MEDIA_768]: {
            gridTemplateColumns: '1fr',
        },
    },

    specCard: {
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceLg,
        textAlign: 'center',
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        ':hover': {
            transform: 'translateY(-2px)',
            borderColor: tokens.secondary,
            boxShadow: tokens.shadowMd,
        },
    },

    specCardTitle: {
        fontSize: tokens.fontSm,
        fontWeight: 600,
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: tokens.spaceSm,
        color: tokens.textPrimary,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },

    specCardText: {
        color: tokens.textSecondary,
        margin: 0,
        fontWeight: 500,
    },

    /* Getting Started Section */
    gettingStartedSection: {
        marginBottom: tokens.space3xl,
    },

    gettingStartedContent: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: tokens.spaceLg,
        [MEDIA_768]: {
            gridTemplateColumns: '1fr',
        },
    },

    stepCard: {
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceXl,
        position: 'relative',
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        ':hover': {
            transform: 'translateY(-2px)',
            borderColor: tokens.accent,
            boxShadow: tokens.shadowMd,
        },
    },

    stepNumber: {
        position: 'absolute',
        top: -20,
        left: tokens.spaceLg,
        width: 40,
        height: 40,
        backgroundColor: tokens.accent,
        color: tokens.textOnAccent,
        borderRadius: tokens.radiusFull,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: tokens.fontLg,
    },

    stepCardTitle: {
        fontSize: tokens.fontLg,
        fontWeight: 600,
        marginTop: tokens.spaceLg,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: tokens.spaceSm,
        color: tokens.textPrimary,
    },

    stepCardText: {
        color: tokens.textSecondary,
        lineHeight: 1.6,
        margin: 0,
    },

    /* Resources Section */
    resourcesSection: {
        marginBottom: tokens.space3xl,
    },

    resourcesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: tokens.spaceLg,
        [MEDIA_768]: {
            gridTemplateColumns: '1fr',
        },
    },

    resourceLink: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spaceMd,
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceLg,
        color: tokens.textPrimary,
        textDecoration: 'none',
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        ':hover': {
            transform: 'translateY(-2px)',
            borderColor: tokens.primary,
            boxShadow: tokens.shadowMd,
            color: tokens.primary,
        },
    },

    resourceLinkIcon: {
        color: tokens.accent,
        flexShrink: 0,
    },

    resourceLinkText: {
        fontWeight: 500,
    },

    /* Image Modal Styles */
    imageModalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: tokens.spaceLg,
    },

    imageModal: {
        backgroundColor: tokens.bgCard,
        borderRadius: tokens.radiusLg,
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: tokens.shadowXl,
        [MEDIA_768]: {
            maxWidth: '95vw',
            maxHeight: '95vh',
        },
    },

    modalCloseBtn: {
        position: 'absolute',
        top: tokens.spaceMd,
        right: tokens.spaceMd,
        backgroundColor: tokens.bgCard,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: tokens.border,
        borderRadius: tokens.radiusFull,
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: tokens.textPrimary,
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        zIndex: 10,
        ':hover': {
            backgroundColor: 'var(--bg-hover)',
            borderColor: tokens.primary,
            color: tokens.primary,
        },
    },

    modalContent: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 0,
        height: '100%',
        [MEDIA_768]: {
            gridTemplateColumns: '1fr',
            gridTemplateRows: 'auto 1fr',
        },
    },

    modalImageSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tokens.bg,
        padding: tokens.spaceLg,
        [MEDIA_768]: {
            padding: tokens.spaceMd,
        },
    },

    modalImage: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        borderRadius: tokens.radiusMd,
    },

    modalPromptSection: {
        backgroundColor: tokens.bgCard,
        padding: tokens.spaceXl,
        display: 'flex',
        flexDirection: 'column',
        borderLeftWidth: 1,
        borderLeftStyle: 'solid',
        borderLeftColor: tokens.border,
        [MEDIA_768]: {
            borderLeftWidth: 0,
            borderTopWidth: 1,
            borderTopStyle: 'solid',
            borderTopColor: tokens.border,
            padding: tokens.spaceLg,
        },
    },

    promptHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: tokens.spaceLg,
        paddingBottom: tokens.spaceMd,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.border,
    },

    promptHeaderTitle: {
        fontSize: tokens.fontLg,
        fontWeight: 600,
        color: tokens.textPrimary,
        margin: 0,
    },

    copyPromptBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spaceSm,
        backgroundColor: tokens.primary,
        color: tokens.textOnAccent,
        borderWidth: 0,
        borderRadius: tokens.radiusMd,
        paddingTop: tokens.spaceSm,
        paddingBottom: tokens.spaceSm,
        paddingLeft: tokens.spaceMd,
        paddingRight: tokens.spaceMd,
        fontSize: tokens.fontSm,
        fontWeight: 500,
        cursor: 'pointer',
        transitionProperty: 'all',
        transitionDuration: tokens.transitionNormal,
        ':hover': {
            backgroundColor: tokens.primaryHover,
            transform: 'translateY(-1px)',
        },
        ':active': {
            transform: 'scale(0.95)',
        },
    },

    copyPromptBtnCopied: {
        backgroundColor: tokens.success,
        animationName: copySuccess,
        animationDuration: '0.6s',
        animationTimingFunction: 'ease-in-out',
    },

    promptContent: {
        flex: 1,
        overflowY: 'auto',
    },

    promptContentText: {
        color: tokens.textSecondary,
        lineHeight: 1.6,
        margin: 0,
        fontSize: tokens.fontSm,
    },
});